import React, { useState, useEffect, useMemo } from 'react';
import AddTaskForm from '../Components/AddTaskForm';
import { useGlobalState } from '../contexts/GlobalContext';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import '../Stylesheets/Tasks.css';
import { apiFetch } from '../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';

const sortTasksByDueDateAsc = (items) => {
  const toTimestamp = (task) => {
    const raw = task?.dueDate ?? task?.due_date ?? null;
    if (!raw) return Number.POSITIVE_INFINITY;
    const ts = new Date(raw).getTime();
    return Number.isNaN(ts) ? Number.POSITIVE_INFINITY : ts;
  };

  return [...items].sort((a, b) => {
    const dueDiff = toTimestamp(a) - toTimestamp(b);
    if (dueDiff !== 0) return dueDiff;
    return String(a.taskType || '').localeCompare(String(b.taskType || ''));
  });
};

function Tasks() {
  const { state, fetchContacts } = useGlobalState();
  const contacts = state.contacts;
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('open');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [focusedTaskId, setFocusedTaskId] = useState(null);
  // Ensure contacts are loaded to display tasks
  useEffect(() => {
    if (state.user?.id && state.contacts.length === 0) {
      fetchContacts();
    }
  }, [state.user]);

  useEffect(() => {
    const allTasks = contacts.flatMap((contact) =>
      (contact.tasks || []).map((task) => ({
        ...task,
        taskId: task.taskId ?? task.id ?? task.task_id ?? task.taskID,
        contactId: contact.id || contact.contactId,
        contactName: contact.name || `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim(),
      }))
    );
    setTasks(sortTasksByDueDateAsc(allTasks));
  }, [contacts]);

  useEffect(() => {
    if (location.state?.focusTaskId) {
      setActiveTab('open');
      setFocusedTaskId(String(location.state.focusTaskId));
    }
  }, [location.state?.focusTaskId]);

  useEffect(() => {
    if (!focusedTaskId || activeTab !== 'open') return;

    const timer = setTimeout(() => {
      const target = document.getElementById(`task-${focusedTaskId}`);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [focusedTaskId, activeTab, tasks]);

  const handleAddTask = (newTask) => {
    const contactMeta = contacts.find(
      (contact) => String(contact.id || contact.contactId) === String(newTask.contactId)
    );

    const enrichedTask = {
      ...newTask,
      taskId: newTask.taskId ?? Date.now(),
      status: newTask.status || 'open',
      contactName:
        contactMeta?.name ||
        `${contactMeta?.first_name ?? ''} ${contactMeta?.last_name ?? ''}`.trim() ||
        newTask.contactName,
    };

    setTasks((prev) => sortTasksByDueDateAsc([...prev, enrichedTask]));
  };

  const handleEditTask = async (taskId, updatedFields) => {
    try {
      const updated = { ...editTask, ...updatedFields };
      const payload = {
        task_id: taskId,
        task_type: updated.taskType,
        task_description: updated.taskDescription,
        due_date: updated.dueDate,
        urgency: updated.urgency,
        status: updated.status,
      };

      const result = await apiFetch('updateTask', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      
      if (result.success) {
        setTasks((prev) =>
          sortTasksByDueDateAsc(
            prev.map((task) => (task.taskId === taskId ? { ...task, ...updated } : task))
          )
        );
        setShowEditModal(false);
      } else {
        alert(result.message);
      }
    } catch (err) {
      alert('Error updating task: ' + err.message);
    }
  };

  const handleDeleteTask = async (taskId) => {
    const result = await apiFetch('deleteTask', {
      method: 'POST',
      body: JSON.stringify({ task_id: taskId }),
    });
    
    if (result.success) {
      setTasks((prev) => prev.filter((t) => t.taskId !== taskId));
    } else {
      alert(result.message);
    }
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => (activeTab === 'open' ? t.status === 'open' : t.status === 'closed'));
  }, [activeTab, tasks]);

  const openEdit = (task) => {
    setEditTask(task);
    setShowEditModal(true);
  };
  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Tasks</h2>
        <div>
          <button className="btn btn-outline-primary me-2" onClick={() => setActiveTab('open')}>Open</button>
          <button className="btn btn-outline-secondary" onClick={() => setActiveTab('closed')}>Closed</button>
        </div>
      </div>

      <AddTaskForm onAddTask={handleAddTask} />

      <div className="mt-3">
        {filteredTasks.length === 0 ? (
          <p className="text-muted text-center">No tasks found for this tab.</p>
        ) : (
          <div className="list-group shadow-sm">
            {filteredTasks.map((task) => {
              const normalizedTaskId = String(task.taskId ?? task.id ?? task.task_id ?? task.taskID);
              const isFocused = focusedTaskId === normalizedTaskId;
              const taskUrgency = String(task.urgency || 'low').toLowerCase();

              return (
                <div
                  key={normalizedTaskId}
                  id={`task-${normalizedTaskId}`}
                  className={`list-group-item d-flex justify-content-between align-items-start${isFocused ? ' task-highlight' : ''}`}
                >
                  <div className="flex-grow-1 me-3">
                    <div className={`task-card-header task-urgency-${taskUrgency}`}>
                      <span className="task-card-title">{task.taskType || 'Task'}</span>
                      <span className="task-card-urgency">{taskUrgency}</span>
                    </div>
                    <div className="task-card-body">
                      <p className="task-card-description mb-1">{task.taskDescription}</p>
                      <small className="text-muted d-block">
                        Contact:{' '}
                        {task.contactId ? (
                          <button
                            type="button"
                            className="btn btn-link p-0 align-baseline"
                            onClick={() => navigate(`/contact/${task.contactId}`)}
                          >
                            {task.contactName || 'View Contact'}
                          </button>
                        ) : (
                          <span>{task.contactName || 'Unknown'}</span>
                        )}
                        {task.dueDate && (
                          <span>
                            {' '}
                            | Due: {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        )}
                      </small>
                    </div>
                  </div>
                  <Dropdown>
                    <Dropdown.Toggle variant="light" size="sm" />
                    <Dropdown.Menu>
                      {task.status === 'open' && (
                        <Dropdown.Item onClick={() => handleEditTask(task.taskId, { ...task, status: 'closed' })}>
                          Mark Complete
                        </Dropdown.Item>
                      )}
                      <Dropdown.Item onClick={() => openEdit(task)}>Edit</Dropdown.Item>
                      <Dropdown.Item className="text-danger" onClick={() => handleDeleteTask(task.taskId)}>
                        Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
              </div>
            );
          })}
          </div>
        )}
      </div>


      {/* Edit Task Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Task</Modal.Title></Modal.Header>
        <Modal.Body>
          {editTask && (
            <>
              <label>Type</label>
              <select className="form-select mb-2" value={editTask.taskType}
                onChange={(e) => setEditTask({ ...editTask, taskType: e.target.value })}>
                <option value="Call">Call</option>
                <option value="Text">Text</option>
                <option value="Email">Email</option>
                <option value="Appointment">Appointment</option>
                <option value="Closing">Closing</option>
                <option value="Inspection">Inspection</option>
              </select>
              <label>Description</label>
              <textarea className="form-control mb-2" value={editTask.taskDescription}
                onChange={(e) => setEditTask({ ...editTask, taskDescription: e.target.value })} />
              <label>Due Date</label>
              <input type="date" className="form-control mb-2" value={editTask.dueDate}
                onChange={(e) => setEditTask({ ...editTask, dueDate: e.target.value })} />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={() => handleEditTask(editTask.taskId, editTask)}>Save</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Tasks;
